import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { streamFlowApi } from '@lib/streamFlowApi';
import ConcurrencyGauge from '@components/ConcurrencyGauge';

/**
 * Operator-only LIVE "who is streaming what" dashboard + per-customer
 * drill-down for troubleshooting. Server gates on isAdminEmail, so a
 * non-admin account just gets 403s here. Auto-refreshes every 10s.
 *
 * See [docs/ADMIN_DASHBOARD.md].
 */
type Stream = NonNullable<Awaited<ReturnType<typeof streamFlowApi.adminActiveStreams>>['data']>[number];
type HouseholdDetail = NonNullable<Awaited<ReturnType<typeof streamFlowApi.adminHousehold>>['data']>;
type ProbeResult = NonNullable<Awaited<ReturnType<typeof streamFlowApi.adminProbeChannel>>['data']>;

export default function AdminStreaming() {
  const navigate = useNavigate();
  const [streams, setStreams] = useState<Stream[]>([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<HouseholdDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const refresh = useCallback(async () => {
    const res = await streamFlowApi.adminActiveStreams();
    if (res.success && res.data) setStreams(res.data);
    else if (res.error) toast.error(res.error.message);
    setLoading(false);
  }, []);

  useEffect(() => {
    void refresh();
    const t = setInterval(() => void refresh(), 10_000); // live-ish
    return () => clearInterval(t);
  }, [refresh]);

  const openHousehold = useCallback(async (id: string | null) => {
    if (!id) return;
    setDetailLoading(true);
    const res = await streamFlowApi.adminHousehold(id);
    if (res.success && res.data) setDetail(res.data);
    else toast.error(res.error?.message ?? 'Failed to load customer');
    setDetailLoading(false);
  }, []);

  // Kill-switch a seat from the live table. Confirm first (destructive),
  // then disconnect + refresh so the row drops on the next poll.
  const revokeSeat = useCallback(async (seatId: string, who: string) => {
    if (!window.confirm(`Force-disconnect ${who}'s stream now? This kills the active connection and invalidates their stream token.`)) return;
    const res = await streamFlowApi.adminRevokeSeat(seatId);
    if (res.success) {
      toast.success(`Seat revoked${res.data ? ` (${res.data.refs_cleared} ref(s) cleared)` : ''}`);
      void refresh();
    } else {
      toast.error(res.error?.message ?? 'Revoke failed');
    }
  }, [refresh]);

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Live Streams</h1>
          <p className="text-sm text-gray-500">Who's watching what, right now — across all households. Auto-refreshes every 10s.</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-1.5 text-sm text-gray-600">
            <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" /> {streams.length} active
          </span>
          <button onClick={() => void refresh()} className="text-sm text-blue-600 hover:underline">Refresh</button>
          <button onClick={() => navigate('/admin/cost')} className="text-sm text-blue-600 hover:underline">Cost ▸</button>
          <button onClick={() => navigate('/admin/status')} className="text-sm text-blue-600 hover:underline">Status ▸</button>
          <button onClick={() => navigate('/admin/audit')} className="text-sm text-blue-600 hover:underline">Audit ▸</button>
          <button onClick={() => navigate('/')} className="text-sm text-blue-600 hover:underline">← Dashboard</button>
        </div>
      </div>

      {loading && <div className="text-sm text-gray-500">Loading…</div>}

      <ConcurrencyGauge />

      <div className="rounded-lg border border-gray-200 overflow-hidden mb-6">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left">
            <tr>
              <th className="px-3 py-2 font-medium">Customer</th>
              <th className="px-3 py-2 font-medium">Channel</th>
              <th className="px-3 py-2 font-medium">Provider</th>
              <th className="px-3 py-2 font-medium">Egress node</th>
              <th className="px-3 py-2 font-medium">Since</th>
              <th className="px-3 py-2 font-medium">Device</th>
              <th className="px-3 py-2 font-medium" />
            </tr>
          </thead>
          <tbody>
            {streams.map((s) => (
              <tr key={s.seat_id} className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-3 py-2">
                  <div className="font-medium">{s.household}</div>
                  <div className="text-xs text-gray-500">{s.member}</div>
                </td>
                <td className="px-3 py-2">{s.channel}</td>
                <td className="px-3 py-2">{s.provider}</td>
                <td className="px-3 py-2"><NodeBadge node={s.node} /></td>
                <td className="px-3 py-2 text-gray-600">{ago(s.since)}</td>
                <td className="px-3 py-2 text-gray-600">{s.device ?? '—'}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <button onClick={() => void openHousehold(s.household_id)} className="text-xs text-blue-600 hover:underline">Troubleshoot ▸</button>
                  <button onClick={() => void revokeSeat(s.seat_id, s.member)} className="ml-3 text-xs text-rose-600 hover:underline">Revoke</button>
                </td>
              </tr>
            ))}
            {!loading && streams.length === 0 && (
              <tr><td className="px-3 py-8 text-center text-gray-500" colSpan={7}>No active streams right now.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {detailLoading && <div className="text-sm text-gray-500">Loading customer…</div>}
      {detail && (
        <HouseholdPanel
          detail={detail}
          onClose={() => setDetail(null)}
          onRefresh={() => void openHousehold(detail.household.id)}
        />
      )}
    </div>
  );
}

function HouseholdPanel({ detail, onClose, onRefresh }: { detail: HouseholdDetail; onClose: () => void; onRefresh: () => void }) {
  const [busy, setBusy] = useState<string | null>(null);
  // Per-channel mirror probe: which channel id is in-flight, and the last
  // result keyed by channel id so it renders inline under the row that
  // triggered it. Lets the operator tell "dead upstream" from "our bug".
  const [probing, setProbing] = useState<string | null>(null);
  const [probeResults, setProbeResults] = useState<Record<string, ProbeResult>>({});
  const navigate = useNavigate();

  // Probe one channel's mirrors. Hits the server probe endpoint, which
  // tries each source/provider and reports whether any is alive.
  const probeChannel = async (channelId: string) => {
    setProbing(channelId);
    const res = await streamFlowApi.adminProbeChannel(channelId);
    setProbing(null);
    if (res.success && res.data) {
      setProbeResults((prev) => ({ ...prev, [channelId]: res.data! }));
    } else {
      toast.error(res.error?.message ?? 'Probe failed');
    }
  };

  // Re-sync one provider's channel catalog (fire-and-forget server-side).
  const resyncProvider = async (providerId: string, name: string) => {
    if (!window.confirm(`Re-pull the channel catalog for "${name}"? Runs in the background; can take a minute for large Xtream lists.`)) return;
    setBusy(providerId);
    const res = await streamFlowApi.adminResyncProvider(providerId);
    setBusy(null);
    if (res.success) toast.success(`Re-sync started for "${name}"`);
    else toast.error(res.error?.message ?? 'Re-sync failed');
  };

  // Manual subscription: grant access time (offline-payment model). Extends
  // from the later of now and the existing period end, then refreshes so the
  // new expiry shows. `label` is just for the toast/confirm copy.
  const grantSubscription = async (grant: { months?: number; days?: number }, label: string) => {
    if (!window.confirm(`Grant ${detail.household.name} ${label} of access? This marks them active and extends their period.`)) return;
    setBusy('grant');
    const res = await streamFlowApi.adminGrantSubscription(detail.household.id, grant);
    setBusy(null);
    if (res.success) {
      toast.success(`Granted ${label} — active through ${res.data ? new Date(res.data.current_period_end).toLocaleDateString() : 'updated'}`);
      onRefresh();
    } else {
      toast.error(res.error?.message ?? 'Grant failed');
    }
  };

  // Custom duration via prompt (e.g. an odd offline period).
  const grantCustom = async () => {
    const raw = window.prompt('Grant how many days of access?', '30');
    if (raw === null) return;
    const days = Math.floor(Number(raw));
    if (!Number.isFinite(days) || days < 1) { toast.error('Enter a positive number of days'); return; }
    await grantSubscription({ days }, `${days} day(s)`);
  };

  // Revoke access (manual model): cancel + expire now → TVs hit the lapse screen.
  const revokeSubscription = async () => {
    if (!window.confirm(`Revoke ${detail.household.name}'s access now? Their TVs will drop to the renew/lapse screen. Reversible — grant time again to restore.`)) return;
    setBusy('revoke');
    const res = await streamFlowApi.adminRevokeSubscription(detail.household.id);
    setBusy(null);
    if (res.success) {
      toast.success('Access revoked');
      onRefresh();
    } else {
      toast.error(res.error?.message ?? 'Revoke failed');
    }
  };

  // Push a real-time toast to the household's TVs (control channel).
  const pushMessage = async () => {
    const text = window.prompt(`Message to show on ${detail.household.name}'s TVs:`);
    if (!text || !text.trim()) return;
    setBusy('msg');
    const res = await streamFlowApi.adminPushMessage(detail.household.id, text.trim());
    setBusy(null);
    if (res.success) toast.success(`Sent — reached ${res.data?.reached ?? 0} device(s)`);
    else toast.error(res.error?.message ?? 'Push failed');
  };

  // Force every TV in the household back to the pair screen.
  const forceRepair = async () => {
    if (!window.confirm(`Force ALL of ${detail.household.name}'s TVs back to the pair screen? They'll have to re-pair. Use for a lost/stolen device or a stuck session.`)) return;
    const reason = window.prompt('Reason (shown briefly on the TV, optional):') ?? undefined;
    setBusy('repair');
    const res = await streamFlowApi.adminForceRepair(detail.household.id, reason || undefined);
    setBusy(null);
    if (res.success) toast.success(`Sent — reached ${res.data?.reached ?? 0} device(s)`);
    else toast.error(res.error?.message ?? 'Force re-pair failed');
  };

  return (
    <div className="rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
        <div>
          <h2 className="font-semibold">{detail.household.name}</h2>
          <span className="text-xs text-gray-500">
            subscription: <span className={detail.household.subscription_status === 'active' || detail.household.subscription_status === 'trialing' ? 'text-emerald-600 font-medium' : 'text-rose-600 font-medium'}>{detail.household.subscription_status}</span>
            {detail.household.current_period_end && ` · through ${new Date(detail.household.current_period_end).toLocaleDateString()}`}
            {detail.household.subscription_method && detail.household.subscription_method !== 'none' && ` · ${detail.household.subscription_method}`}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(`/admin/support/${detail.household.id}`)}
            className="rounded-md border border-violet-300 bg-violet-50 px-3 py-1.5 text-sm text-violet-900 hover:bg-violet-100"
          >
            Screen view
          </button>
          <button
            onClick={() => void pushMessage()}
            disabled={busy === 'msg'}
            className="rounded-md border border-blue-300 bg-blue-50 px-3 py-1.5 text-sm text-blue-900 hover:bg-blue-100 disabled:opacity-50"
          >
            {busy === 'msg' ? 'Sending…' : 'Message'}
          </button>
          <button
            onClick={() => void forceRepair()}
            disabled={busy === 'repair'}
            className="rounded-md border border-rose-300 bg-rose-50 px-3 py-1.5 text-sm text-rose-900 hover:bg-rose-100 disabled:opacity-50"
          >
            {busy === 'repair' ? 'Sending…' : 'Force re-pair'}
          </button>
          <button onClick={onClose} className="text-sm text-gray-500 hover:underline">Close</button>
        </div>
      </div>

      {/* Manual subscription — offline-payment model: the operator grants
          access time by hand and can revoke it. No payment processor. */}
      <div className="px-4 py-3 border-b border-gray-100 bg-emerald-50/40">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-gray-700 mr-1">Subscription (manual):</span>
          <button onClick={() => void grantSubscription({ days: 30 }, '30 days')} disabled={busy === 'grant'}
            className="rounded-md border border-emerald-300 bg-white px-2.5 py-1 text-sm text-emerald-900 hover:bg-emerald-100 disabled:opacity-50">+30 days</button>
          <button onClick={() => void grantSubscription({ months: 1 }, '1 month')} disabled={busy === 'grant'}
            className="rounded-md border border-emerald-300 bg-white px-2.5 py-1 text-sm text-emerald-900 hover:bg-emerald-100 disabled:opacity-50">+1 month</button>
          <button onClick={() => void grantSubscription({ months: 3 }, '3 months')} disabled={busy === 'grant'}
            className="rounded-md border border-emerald-300 bg-white px-2.5 py-1 text-sm text-emerald-900 hover:bg-emerald-100 disabled:opacity-50">+3 months</button>
          <button onClick={() => void grantSubscription({ months: 6 }, '6 months')} disabled={busy === 'grant'}
            className="rounded-md border border-emerald-300 bg-white px-2.5 py-1 text-sm text-emerald-900 hover:bg-emerald-100 disabled:opacity-50">+6 months</button>
          <button onClick={() => void grantSubscription({ months: 12 }, '12 months')} disabled={busy === 'grant'}
            className="rounded-md border border-emerald-300 bg-white px-2.5 py-1 text-sm text-emerald-900 hover:bg-emerald-100 disabled:opacity-50">+12 months</button>
          <button onClick={() => void grantCustom()} disabled={busy === 'grant'}
            className="rounded-md border border-gray-300 bg-white px-2.5 py-1 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50">Custom…</button>
          <span className="flex-1" />
          <button onClick={() => void revokeSubscription()} disabled={busy === 'revoke'}
            className="rounded-md border border-rose-300 bg-white px-2.5 py-1 text-sm text-rose-700 hover:bg-rose-100 disabled:opacity-50">
            {busy === 'revoke' ? 'Revoking…' : 'Revoke access'}
          </button>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4 p-4">
        <section>
          <h3 className="text-sm font-semibold mb-2">Providers</h3>
          <div className="space-y-1.5">
            {detail.providers.map((p) => (
              <div key={p.id} className="text-sm flex items-start gap-2">
                <HealthDot status={p.health_status} />
                <div className="flex-1">
                  <div className="font-medium">{p.name} <span className="text-xs text-gray-400">({p.provider_type})</span></div>
                  {p.health_detail && <div className="text-xs text-gray-500">{p.health_detail}</div>}
                </div>
                <button
                  onClick={() => void resyncProvider(p.id, p.name)}
                  disabled={busy === p.id}
                  className="text-xs text-blue-600 hover:underline disabled:opacity-50 whitespace-nowrap"
                >
                  {busy === p.id ? 'Re-syncing…' : 'Re-sync'}
                </button>
              </div>
            ))}
            {detail.providers.length === 0 && <div className="text-xs text-gray-500">No providers.</div>}
          </div>
        </section>
        <section>
          <h3 className="text-sm font-semibold mb-2">Recently watched</h3>
          <div className="space-y-1 text-sm">
            {detail.recent_watches.slice(0, 8).map((w, i) => {
              // Probe is per-channel, so it needs the channel UUID. Prefer the
              // flat channel_id; fall back to the joined sf_channels.id. Older
              // rows that carry neither just don't get a Probe button.
              const channelId = w.channel_id ?? w.sf_channels?.id ?? null;
              const result = channelId ? probeResults[channelId] : undefined;
              return (
                <div key={i}>
                  <div className="flex justify-between gap-2 items-center">
                    <span className="truncate">{w.sf_channels?.name ?? '(channel)'}</span>
                    <span className="flex items-center gap-2 whitespace-nowrap">
                      {channelId && (
                        <button
                          onClick={() => void probeChannel(channelId)}
                          disabled={probing === channelId}
                          className="text-xs text-blue-600 hover:underline disabled:opacity-50"
                        >
                          {probing === channelId ? 'Probing…' : 'Probe'}
                        </button>
                      )}
                      <span className="text-xs text-gray-400">{ago(w.started_at)}</span>
                    </span>
                  </div>
                  {result && <ProbeReport result={result} />}
                </div>
              );
            })}
            {detail.recent_watches.length === 0 && <div className="text-xs text-gray-500">No history.</div>}
          </div>
        </section>
        <section>
          <h3 className="text-sm font-semibold mb-2">Devices (24h fingerprints)</h3>
          <div className="space-y-1 text-sm">
            {detail.devices.slice(0, 8).map((d, i) => (
              <div key={i} className="flex justify-between gap-2 text-xs">
                <span className="font-mono">{d.ip_prefix}</span>
                <span className="text-gray-400">{d.hit_count} hits · {ago(d.last_seen_at)}</span>
              </div>
            ))}
            {detail.devices.length === 0 && <div className="text-xs text-gray-500">No device fingerprints.</div>}
            {detail.devices.length > 6 && <div className="text-xs text-amber-600">⚠ {detail.devices.length} distinct devices — possible sharing.</div>}
          </div>
        </section>
        <section>
          <h3 className="text-sm font-semibold mb-2">Pairings</h3>
          <div className="space-y-1 text-sm">
            {detail.pairings.slice(0, 6).map((p, i) => (
              <div key={i} className="flex justify-between gap-2 text-xs">
                <span>{p.device_label ?? p.code}</span>
                <span className={p.redeemed_at ? 'text-emerald-600' : 'text-amber-600'}>{p.redeemed_at ? 'paired' : 'pending'}</span>
              </div>
            ))}
            {detail.pairings.length === 0 && <div className="text-xs text-gray-500">No pairings.</div>}
          </div>
        </section>
      </div>
    </div>
  );
}

// Inline mirror-probe result for one channel: a summary line derived from
// any_alive + the mirror list, then one row per source (colored dot + provider
// + HTTP probe status + egress + alive/dead). Reuses HealthDot for the dot —
// a live mirror maps to 'healthy' (green), a dead one to 'unhealthy' (red).
function ProbeReport({ result }: { result: ProbeResult }) {
  const aliveCount = result.mirrors.filter((m) => m.alive).length;
  const total = result.mirrors.length;
  return (
    <div className="mt-1 mb-2 ml-2 pl-2 border-l-2 border-gray-200 space-y-0.5">
      <div className={`text-xs font-medium ${result.any_alive ? 'text-emerald-600' : 'text-rose-600'}`}>
        {result.any_alive
          ? `✓ ${aliveCount} of ${total} source(s) alive`
          : '✗ all sources dead — channel is down upstream'}
      </div>
      {result.mirrors.map((m) => (
        <div key={m.provider_id} className="flex items-center gap-2 text-xs text-gray-600">
          <HealthDot status={m.alive ? 'healthy' : 'unhealthy'} />
          <span className="font-medium text-gray-700">{m.provider_name}</span>
          <span className="text-gray-400">via {m.egress}</span>
          <span className="font-mono text-gray-500">{m.probe_status ?? 'no resp'}</span>
          <span className={m.alive ? 'text-emerald-600' : 'text-rose-600'}>{m.alive ? 'alive' : 'dead'}</span>
        </div>
      ))}
      {total === 0 && <div className="text-xs text-gray-500">No mirrors configured for this channel.</div>}
    </div>
  );
}
function NodeBadge({ node }: { node: string }) {
  const isPublic = /public|azure-edge/i.test(node);
  return <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] ${isPublic ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}`}>{node}</span>;
}
function HealthDot({ status }: { status: string }) {
  const c = status === 'healthy' ? 'bg-emerald-500' : status === 'unhealthy' ? 'bg-rose-500' : 'bg-gray-300';
  return <span className={`mt-1 h-2 w-2 rounded-full shrink-0 ${c}`} />;
}
function ago(iso: string | null): string {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return 'soon';
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}
